import { useDataProvider, useOnError } from '@refinedev/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  CHANGE_LOG_PAGE_SIZE,
  firstCursor,
  hasMoreHistory,
  historySources,
  loadHistoryStep,
  type CustomGet,
  type HistoryCursor,
  type HistoryRequests,
  type HistoryStep,
} from './changeLogClient';
import type { ChangeLogSubject } from './changeLogTypes';

/** One cache entry per company, role or person: never another one's history. */
export const changeLogQueryKey = (subject: ChangeLogSubject) => {
  if (subject.kind === 'company') {
    return ['change-log', 'company', subject.companyId, subject.includeSso === true];
  }
  if (subject.kind === 'role') return ['change-log', 'role', subject.roleId];
  return ['change-log', 'user', subject.authUserId, subject.email?.trim().toLowerCase() ?? ''];
};

/**
 * The subject's change log, loaded a page per source at a time. Used only inside the open
 * drawer, so it is read afresh every time the drawer opens.
 */
export function useChangeLog(subject: ChangeLogSubject) {
  const getDataProvider = useDataProvider();
  // Failures go to the auth provider like every useCustom read (a dead session logs out).
  const { mutate: checkError } = useOnError();
  const customOf =
    (name?: string): CustomGet =>
    (args) => {
      const custom = getDataProvider(name).custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      return custom(args);
    };
  const requests: HistoryRequests = {
    platform: customOf(),
    auth: customOf('authDataProvider'),
  };
  const sources = historySources(subject, requests);
  return useInfiniteQuery<HistoryStep>({
    queryKey: changeLogQueryKey(subject),
    queryFn: ({ pageParam }) =>
      loadHistoryStep(
        sources,
        (pageParam as HistoryCursor | undefined) ?? firstCursor(sources),
        CHANGE_LOG_PAGE_SIZE,
        (error) => checkError(error),
      ),
    getNextPageParam: (last) => (hasMoreHistory(last.next) ? last.next : undefined),
    // Refine turns keepPreviousData on for every query; a new subject starts empty.
    keepPreviousData: false,
    // Nothing kept once the drawer closes: every open reads afresh and starts at page one.
    cacheTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
